import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Check, Search, ExternalLink, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Info, Copy, Share2, X } from "lucide-react";
import { generateFingerprint, hasCompletedSurvey, markSurveyCompleted, getCompletedSurveys } from "@/utils/fingerprint";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SurveyPage = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [fingerprint, setFingerprint] = useState("");
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [showCompleted, setShowCompleted] = useState(true);
  const [showPolicyDetails, setShowPolicyDetails] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  const SURVEY_URL = window.location.origin + "/survey";
  const SHARE_PITCH = "Help with a student research project! This 2-minute survey studies how different age groups react to U.S. policy changes. No login needed, completely anonymous.";
  
  const [formData, setFormData] = useState({
    email: "",
    us_resident: "",
    age_group: "",
    income_level: "",
    investment_experience: "",
    emotional_responses: {
      confidence: 5,
      excitement: 5,
      anxiety: 5
    },
    behavioral_responses: {
      risk_appetite: "",
      investment_change: "",
      asset_allocation_shift: ""
    },
    additional_comments: ""
  });

  useEffect(() => {
    const fp = generateFingerprint();
    setFingerprint(fp);
    
    fetch(`${API}/policies`)
      .then(res => res.json())
      .then(data => {
        setPolicies(data);
        // Update completed surveys list with policy details
        const completedIds = getCompletedSurveys();
        const completedPolicies = data.filter(p => completedIds.includes(p.id));
        setCompletedSurveys(completedPolicies);
      })
      .catch(err => {
        console.error(err);
        toast.error("Failed to load policies");
      });
  }, []);

  const categories = ["all", ...new Set(policies.map(p => p.category).filter(Boolean))];

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = 
      policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || policy.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectPolicy = (policyId) => {
    if (hasCompletedSurvey(policyId)) {
      toast.warning("You have already completed this survey");
      return;
    }
    setSelectedPolicyId(policyId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPolicyId) {
      toast.error("Please select a policy");
      return;
    }

    if (!formData.age_group) {
      toast.error("Please select your age group");
      return;
    }

    if (!formData.us_resident) {
      toast.error("Please indicate if you are a U.S. resident");
      return;
    }

    if (!formData.behavioral_responses.risk_appetite || 
        !formData.behavioral_responses.investment_change || 
        !formData.behavioral_responses.asset_allocation_shift) {
      toast.error("Please complete all behavioral response fields");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_id: selectedPolicyId,
          fingerprint,
          ...formData
        })
      });

      if (response.ok) {
        markSurveyCompleted(selectedPolicyId);
        const completedPolicy = policies.find(p => p.id === selectedPolicyId);
        setCompletedSurveys(prev => [...prev, completedPolicy]);
        toast.success("Survey submitted successfully! Thank you for your contribution.");
        setShowShareModal(true);
        setSelectedPolicyId(null);
        // Reset form
        setFormData({
          email: formData.email, // Keep email
          us_resident: formData.us_resident, // Keep residency
          age_group: formData.age_group, // Keep demographics
          income_level: formData.income_level,
          investment_experience: formData.investment_experience,
          emotional_responses: { confidence: 5, excitement: 5, anxiety: 5 },
          behavioral_responses: { risk_appetite: "", investment_change: "", asset_allocation_shift: "" },
          additional_comments: ""
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit survey");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }

    setLoading(false);
  };

  const selectedPolicy = policies.find(p => p.id === selectedPolicyId);

  // Progress calculation
  const getProgress = () => {
    let filled = 0;
    let total = 7; // policy, us_resident, age, risk, investment, asset, emotions (always filled)
    if (selectedPolicyId) filled++;
    if (formData.us_resident) filled++;
    if (formData.age_group) filled++;
    if (formData.behavioral_responses.risk_appetite) filled++;
    if (formData.behavioral_responses.investment_change) filled++;
    if (formData.behavioral_responses.asset_allocation_shift) filled++;
    filled++; // emotions always have default values
    return Math.round((filled / total) * 100);
  };
  const progress = getProgress();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(SURVEY_URL + "\n\n" + SHARE_PITCH).then(() => {
      setLinkCopied(true);
      toast.success("Link and message copied!");
      setTimeout(() => setLinkCopied(false), 3000);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-transparent to-primary/10">
      <div className="container mx-auto px-6 md:px-12 py-8">
        <Button 
          data-testid="back-button"
          variant="ghost" 
          onClick={() => navigate('/')} 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="mb-6">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Policy Survey</h1>
          <p className="text-lg text-muted-foreground">Select a policy and share your insights</p>
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="share-modal">
            <Card className="max-w-lg w-full rounded-xl relative">
              <button
                onClick={() => setShowShareModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                data-testid="share-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Thank You!</CardTitle>
                <CardDescription className="text-base">
                  Your response has been recorded. Help us reach more people by sharing this survey.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-secondary/50 rounded-lg p-4 text-sm text-muted-foreground" data-testid="share-pitch">
                  "{SHARE_PITCH}"
                </div>
                <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-3 border">
                  <input
                    type="text"
                    readOnly
                    value={SURVEY_URL}
                    className="flex-1 bg-transparent text-sm outline-none"
                    data-testid="share-url-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleCopyLink}
                    data-testid="copy-link-button"
                    className={linkCopied ? "bg-green-600 hover:bg-green-700" : "bg-primary hover:bg-primary/90"}
                  >
                    {linkCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link & Message
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowShareModal(false)}
                    data-testid="share-continue-button"
                  >
                    Continue Surveying
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowShareModal(false);
                      navigate('/');
                    }}
                    data-testid="share-done-button"
                  >
                    I'm Done
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Completed Surveys Section */}
        {completedSurveys.length > 0 && (
          <Card className="border border-green-200 bg-green-50/50 shadow-sm rounded-xl mb-6">
            <CardHeader className="pb-2">
              <button 
                onClick={() => setShowCompleted(!showCompleted)}
                className="flex items-center justify-between w-full"
                data-testid="toggle-completed-surveys"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <CardTitle className="text-lg text-green-800">
                    Your Completed Surveys ({completedSurveys.length})
                  </CardTitle>
                </div>
                {showCompleted ? (
                  <ChevronUp className="w-5 h-5 text-green-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-green-600" />
                )}
              </button>
            </CardHeader>
            {showCompleted && (
              <CardContent className="pt-2">
                <div className="space-y-2">
                  {completedSurveys.map(policy => (
                    <div 
                      key={policy.id}
                      data-testid={`completed-survey-${policy.id}`}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-200"
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800 truncate">{policy.title}</p>
                        <p className="text-xs text-green-600">{policy.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Side - Policies */}
          <div className="lg:col-span-2">
            <Card className="border border-border/50 shadow-lg rounded-xl sticky top-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Select a Policy</CardTitle>
                <CardDescription>Search or browse policies to evaluate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search with Suggestions */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                  <Input
                    data-testid="policy-search-input"
                    type="text"
                    placeholder="Type to search policies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-sm border-2 focus:border-primary"
                  />
                  {searchQuery && filteredPolicies.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-20 max-h-[300px] overflow-y-auto">
                      <div className="p-2 text-xs text-muted-foreground border-b bg-secondary/30">
                        {filteredPolicies.length} {filteredPolicies.length === 1 ? 'result' : 'results'} found
                      </div>
                      {filteredPolicies.slice(0, 6).map(policy => {
                        const isCompleted = hasCompletedSurvey(policy.id);
                        return (
                          <button
                            key={policy.id}
                            type="button"
                            onClick={() => {
                              selectPolicy(policy.id);
                              setSearchQuery("");
                            }}
                            className={`w-full text-left p-3 hover:bg-primary/5 border-b last:border-b-0 transition-colors ${
                              isCompleted ? 'opacity-60' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded">
                                {policy.category}
                              </span>
                              {isCompleted && (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Completed
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-medium line-clamp-1">{policy.title}</p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Categories */}
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                  <TabsList className="flex-wrap h-auto gap-1 bg-secondary/50 p-1 w-full">
                    {categories.map(category => (
                      <TabsTrigger 
                        key={category} 
                        value={category}
                        data-testid={`category-tab-${category}`}
                        className="text-xs capitalize px-2 py-1"
                      >
                        {category === "all" ? "All" : category}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {/* Selected Policy Info */}
                {selectedPolicy && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                    <p className="text-sm font-medium text-primary">
                      Selected: {selectedPolicy.title}
                    </p>
                  </div>
                )}

                {/* All Policies Label */}
                <div className="flex items-center justify-between pt-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    {searchQuery ? `Showing ${filteredPolicies.length} results` : `All Policies (${policies.length})`}
                  </p>
                </div>

                {/* Policy List */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {filteredPolicies.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No policies found</p>
                  ) : (
                    filteredPolicies.map(policy => {
                      const isCompleted = hasCompletedSurvey(policy.id);
                      const isSelected = selectedPolicyId === policy.id;
                      
                      return (
                        <Card
                          key={policy.id}
                          data-testid={`policy-card-${policy.id}`}
                          className={`cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-2 border-primary bg-primary/5 shadow-md' 
                              : isCompleted
                              ? 'border border-green-300 bg-green-50/50 opacity-70'
                              : 'border border-border hover:border-primary/50 hover:shadow-sm'
                          }`}
                          onClick={() => selectPolicy(policy.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                                isSelected 
                                  ? 'border-primary bg-primary' 
                                  : isCompleted
                                  ? 'border-green-500 bg-green-500'
                                  : 'border-gray-300'
                              }`}>
                                {(isSelected || isCompleted) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded">
                                    {policy.category}
                                  </span>
                                  {policy.url && (
                                    <a 
                                      href={policy.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary hover:text-primary/70"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                                <h3 className="font-medium text-sm mb-1 line-clamp-2">{policy.title}</h3>
                                <p className="text-xs text-muted-foreground line-clamp-2">{policy.description}</p>
                                {isCompleted && (
                                  <p className="text-xs text-green-600 mt-2 font-medium flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Completed
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Survey Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit}>
              <Card className="border border-border/50 shadow-lg rounded-xl">
                <CardHeader>
                  <CardTitle className="text-xl">Your Response</CardTitle>
                  <CardDescription>
                    {!selectedPolicyId ? (
                      <span className="flex items-center gap-2 text-orange-600">
                        <AlertCircle className="w-4 h-4" />
                        Please select a policy from the left
                      </span>
                    ) : (
                      <span className="text-primary font-medium">
                        Responding to: {selectedPolicy?.title}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Progress Indicator */}
                  <div className="space-y-2" data-testid="progress-indicator">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground font-medium">Progress</span>
                      <span className="font-mono font-semibold text-primary">{progress}%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                        data-testid="progress-bar"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {progress < 50 ? "Select a policy and fill in your details" : 
                       progress < 100 ? "Almost there! Complete the remaining fields" : 
                       "All set! You can submit your response"}
                    </p>
                  </div>
                  {/* Policy Summary - Read More */}
                  {selectedPolicy && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowPolicyDetails(!showPolicyDetails)}
                        className="w-full flex items-center justify-between p-4 hover:bg-primary/10 transition-colors"
                        data-testid="toggle-policy-details"
                      >
                        <div className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-primary" />
                          <span className="font-medium text-primary">About this Policy</span>
                        </div>
                        {showPolicyDetails ? (
                          <ChevronUp className="w-4 h-4 text-primary" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-primary" />
                        )}
                      </button>
                      {showPolicyDetails && (
                        <div className="px-4 pb-4 space-y-3" data-testid="policy-details-content">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">What is this policy?</p>
                            <p className="text-sm text-foreground">{selectedPolicy.description}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-primary/10">
                            {selectedPolicy.category && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Category</p>
                                <span className="inline-block text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded">
                                  {selectedPolicy.category}
                                </span>
                              </div>
                            )}
                            {selectedPolicy.date_effective && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Effective Date</p>
                                <p className="text-sm text-foreground">{selectedPolicy.date_effective}</p>
                              </div>
                            )}
                          </div>
                          
                          {selectedPolicy.source && (
                            <div className="pt-2 border-t border-primary/10">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Source</p>
                              <p className="text-sm text-muted-foreground italic">{selectedPolicy.source}</p>
                            </div>
                          )}
                          
                          {selectedPolicy.url && (
                            <a
                              href={selectedPolicy.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                              data-testid="policy-source-link"
                            >
                              Read official source
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Demographics */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Your Information</h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email (Optional - for follow-up)</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="email-input"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Are you a U.S. resident? *</Label>
                      <div className="flex gap-3" data-testid="us-resident-group">
                        <button
                          type="button"
                          data-testid="us-resident-yes"
                          onClick={() => setFormData({...formData, us_resident: "yes"})}
                          className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all ${
                            formData.us_resident === "yes"
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border hover:border-primary/50 text-muted-foreground"
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          data-testid="us-resident-no"
                          onClick={() => setFormData({...formData, us_resident: "no"})}
                          className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all ${
                            formData.us_resident === "no"
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border hover:border-primary/50 text-muted-foreground"
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="age-group">Age Group *</Label>
                      <Select value={formData.age_group} onValueChange={(val) => setFormData({...formData, age_group: val})}>
                        <SelectTrigger data-testid="age-group-select" id="age-group" className="h-12">
                          <SelectValue placeholder="Select your age group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="14-18">14-18</SelectItem>
                          <SelectItem value="18-25">18-25</SelectItem>
                          <SelectItem value="26-35">26-35</SelectItem>
                          <SelectItem value="36-50">36-50</SelectItem>
                          <SelectItem value="51-65">51-65</SelectItem>
                          <SelectItem value="65+">65+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="income">Income Level (Optional)</Label>
                        <Select value={formData.income_level} onValueChange={(val) => setFormData({...formData, income_level: val})}>
                          <SelectTrigger data-testid="income-select" id="income" className="h-12">
                            <SelectValue placeholder="Select income" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-30k">Under $30K</SelectItem>
                            <SelectItem value="30k-60k">$30K - $60K</SelectItem>
                            <SelectItem value="60k-100k">$60K - $100K</SelectItem>
                            <SelectItem value="100k-200k">$100K - $200K</SelectItem>
                            <SelectItem value="over-200k">Over $200K</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="experience">Investment Experience (Optional)</Label>
                        <Select value={formData.investment_experience} onValueChange={(val) => setFormData({...formData, investment_experience: val})}>
                          <SelectTrigger data-testid="experience-select" id="experience" className="h-12">
                            <SelectValue placeholder="Select experience" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                            <SelectItem value="expert">Expert</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Emotional Responses */}
                  <div className="space-y-6 border-t pt-6">
                    <h3 className="text-lg font-semibold">Emotional Responses</h3>
                    <p className="text-sm text-muted-foreground">Rate your emotional response (1-10 scale)</p>
                    
                    {['confidence', 'excitement', 'anxiety'].map((emotion) => (
                      <div key={emotion} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="capitalize font-medium">{emotion}</Label>
                          <span className="text-xl font-mono font-semibold text-primary" data-testid={`${emotion}-value`}>
                            {Math.round(formData.emotional_responses[emotion])}
                          </span>
                        </div>
                        <Slider
                          data-testid={`${emotion}-slider`}
                          value={[formData.emotional_responses[emotion]]}
                          onValueChange={(val) => setFormData({
                            ...formData,
                            emotional_responses: { ...formData.emotional_responses, [emotion]: val[0] }
                          })}
                          min={1}
                          max={10}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Very Low</span>
                          <span>Very High</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Behavioral Responses */}
                  <div className="space-y-4 border-t pt-6">
                    <h3 className="text-lg font-semibold">Behavioral Responses</h3>
                    <p className="text-sm text-muted-foreground">How would this policy affect your investment decisions?</p>

                    <div className="space-y-2">
                      <Label htmlFor="risk-appetite">Risk Appetite *</Label>
                      <Select 
                        value={formData.behavioral_responses.risk_appetite} 
                        onValueChange={(val) => setFormData({
                          ...formData,
                          behavioral_responses: { ...formData.behavioral_responses, risk_appetite: val }
                        })}
                      >
                        <SelectTrigger data-testid="risk-appetite-select" id="risk-appetite" className="h-12">
                          <SelectValue placeholder="Select risk appetite" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="much-lower">Much Lower - More risk-averse</SelectItem>
                          <SelectItem value="lower">Lower - More cautious</SelectItem>
                          <SelectItem value="unchanged">Unchanged</SelectItem>
                          <SelectItem value="higher">Higher - More willing to risk</SelectItem>
                          <SelectItem value="much-higher">Much Higher - More aggressive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="investment-change">Investment Change *</Label>
                      <Select 
                        value={formData.behavioral_responses.investment_change} 
                        onValueChange={(val) => setFormData({
                          ...formData,
                          behavioral_responses: { ...formData.behavioral_responses, investment_change: val }
                        })}
                      >
                        <SelectTrigger data-testid="investment-change-select" id="investment-change" className="h-12">
                          <SelectValue placeholder="Select investment change" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="increase-significantly">Increase Significantly (20%+)</SelectItem>
                          <SelectItem value="increase-moderately">Increase Moderately (10-20%)</SelectItem>
                          <SelectItem value="no-change">No Change</SelectItem>
                          <SelectItem value="decrease-moderately">Decrease Moderately (10-20%)</SelectItem>
                          <SelectItem value="decrease-significantly">Decrease Significantly (20%+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="asset-shift">Asset Allocation Shift *</Label>
                      <Select 
                        value={formData.behavioral_responses.asset_allocation_shift} 
                        onValueChange={(val) => setFormData({
                          ...formData,
                          behavioral_responses: { ...formData.behavioral_responses, asset_allocation_shift: val }
                        })}
                      >
                        <SelectTrigger data-testid="asset-shift-select" id="asset-shift" className="h-12">
                          <SelectValue placeholder="Select asset shift" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="to-stocks">Shift to Stocks</SelectItem>
                          <SelectItem value="to-bonds">Shift to Bonds</SelectItem>
                          <SelectItem value="to-cash">Shift to Cash</SelectItem>
                          <SelectItem value="to-alternatives">Shift to Alternatives</SelectItem>
                          <SelectItem value="maintain-current">Maintain Current</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="space-y-2 border-t pt-6">
                    <Label htmlFor="comments">Additional Comments (Optional)</Label>
                    <Textarea
                      data-testid="comments-textarea"
                      id="comments"
                      value={formData.additional_comments}
                      onChange={(e) => setFormData({...formData, additional_comments: e.target.value})}
                      placeholder="Share your thoughts about the selected policy..."
                      className="min-h-[100px] resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="submit-button"
                    disabled={loading || !selectedPolicyId}
                    className="w-full h-12 bg-primary hover:bg-primary/90"
                  >
                    {loading ? 'Submitting...' : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Submit Survey
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
